import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControlProps,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';

interface ChecklistState {
  propertyWatchlistStatus: string;
  employerWatchlistStatus: string;
  matchFilesStatus: string;
  matchFilesNotes?: string;
  propertyTaxAdjustment: string;
  dealBorrowerRelationshipStatus: string;
  dealBorrowerRelationshipOther?: string;
  propertyValueStatus: string;
  propertyValueOtherNotes?: string;
  mortgageAmount: string;
  mtgType: string;
  ltv: string;
  etoChecked: boolean;
  etoNotes?: string;
  term: string;
  rate: string;
  amortization: string;
  ltvRestrictions: string;
  propertyInfo: string;
  province: string;
}

interface PreliminaryChecklistProps {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  checklistState: ChecklistState;
  handleSelectChange: (fieldName: keyof ChecklistState) => (event: SelectChangeEvent) => void;
  handleTextChange: (fieldName: keyof ChecklistState) => (value: string) => void;
  handleCheckboxChange: (fieldName: keyof ChecklistState) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  StyledFormControl: React.ElementType<FormControlProps>;
}

const PreliminaryChecklist: React.FC<PreliminaryChecklistProps> = ({
  expanded,
  onChange,
  checklistState,
  handleSelectChange,
  handleTextChange,
  handleCheckboxChange,
  StyledFormControl
}) => {
  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={{ mb: 2, backgroundColor: 'rgba(240, 245, 250, 0.9)' }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="checklist-content"
        id="checklist-header"
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.03)',
          minHeight: '42px',
          '& .MuiAccordionSummary-content': {
            margin: '8px 0'
          }
        }}
      >
        <Typography variant="h5" fontSize="1.1rem">Preliminary Checklist</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2 }}>
        <Grid container spacing={1}>
          {/* --- Row 1 (7 fields) --- */}
          {/* Field 1: Property */}
          <Grid item xs={12} sm={6} md={1.7}>
            <StyledFormControl fullWidth>
              <InputLabel id="prop-watchlist-label" sx={{ fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' }}>Property</InputLabel>
              <Select
                labelId="prop-watchlist-label"
                name="propertyWatchlistStatus"
                value={checklistState.propertyWatchlistStatus}
                label="Property"
                onChange={handleSelectChange('propertyWatchlistStatus')}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  '.MuiSelect-select': { padding: '4px 14px' },
                  '.MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  cursor: 'pointer'
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="Property on watchlist" sx={{ fontSize: '0.75rem' }}>Property on watchlist</MenuItem>
                <MenuItem value="Property not on watchlist" sx={{ fontSize: '0.75rem' }}>Property not on watchlist</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
          
          {/* Field 2: Property Info */}
          <Grid item xs={12} sm={6} md={1.7}>
            <StyledFormControl fullWidth>
              <InputLabel id="property-info-label" sx={{ fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' }}>Property Info</InputLabel>
              <Select
                labelId="property-info-label"
                name="propertyInfo"
                value={checklistState.propertyInfo}
                label="Property Info"
                onChange={handleSelectChange('propertyInfo')}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  '.MuiSelect-select': { padding: '4px 14px' },
                  '.MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  cursor: 'pointer'
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="Marketable property close to schools, parks and amenities" sx={{ fontSize: '0.75rem' }}>Marketable property</MenuItem>
                <MenuItem value="Property in good condition relatively close to schools, parks and amenities" sx={{ fontSize: '0.75rem' }}>Good condition property</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
          
          {/* Field 3: Property Tax */}
          <Grid item xs={12} sm={6} md={1.7}>
            <StyledFormControl fullWidth>
              <InputLabel id="property-tax-label" sx={{ fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' }}>Property Tax</InputLabel>
              <Select
                labelId="property-tax-label"
                name="propertyTaxAdjustment"
                value={checklistState.propertyTaxAdjustment}
                label="Property Tax"
                onChange={handleSelectChange('propertyTaxAdjustment')}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  '.MuiSelect-select': { padding: '4px 14px' },
                  '.MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  cursor: 'pointer'
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="Property tax needs to be adjusted upward" sx={{ fontSize: '0.75rem' }}>Needs adjustment</MenuItem>
                <MenuItem value="Property tax doesn't need to be adjusted" sx={{ fontSize: '0.75rem' }}>No adjustment</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
          
          {/* Field 4: Property Value */}
          <Grid item xs={12} sm={6} md={1.7}>
            <StyledFormControl fullWidth>
              <InputLabel id="property-value-label" sx={{ fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' }}>Property Value</InputLabel>
              <Select
                labelId="property-value-label"
                name="propertyValueStatus"
                value={checklistState.propertyValueStatus}
                label="Property Value"
                onChange={handleSelectChange('propertyValueStatus')}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  '.MuiSelect-select': { padding: '4px 14px' },
                  '.MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  cursor: 'pointer'
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="Property value reasonable" sx={{ fontSize: '0.75rem' }}>Reasonable</MenuItem>
                <MenuItem value="Property value inflated" sx={{ fontSize: '0.75rem' }}>Inflated</MenuItem>
                <MenuItem value="Other" sx={{ fontSize: '0.75rem' }}>Other</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
          
          {/* Field 5: Province */}
          <Grid item xs={12} sm={6} md={1.7}>
            <StyledFormControl fullWidth>
              <InputLabel id="province-label" sx={{ fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' }}>Province</InputLabel>
              <Select
                labelId="province-label"
                name="province"
                value={checklistState.province}
                label="Province"
                onChange={handleSelectChange('province')}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  '.MuiSelect-select': { padding: '4px 14px' },
                  '.MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  cursor: 'pointer'
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="AB" sx={{ fontSize: '0.75rem' }}>AB</MenuItem>
                <MenuItem value="BC" sx={{ fontSize: '0.75rem' }}>BC</MenuItem>
                <MenuItem value="MB" sx={{ fontSize: '0.75rem' }}>MB</MenuItem>
                <MenuItem value="NB" sx={{ fontSize: '0.75rem' }}>NB</MenuItem>
                <MenuItem value="NL" sx={{ fontSize: '0.75rem' }}>NL</MenuItem>
                <MenuItem value="NS" sx={{ fontSize: '0.75rem' }}>NS</MenuItem>
                <MenuItem value="NT" sx={{ fontSize: '0.75rem' }}>NT</MenuItem>
                <MenuItem value="NU" sx={{ fontSize: '0.75rem' }}>NU</MenuItem>
                <MenuItem value="ON" sx={{ fontSize: '0.75rem' }}>ON</MenuItem>
                <MenuItem value="PE" sx={{ fontSize: '0.75rem' }}>PE</MenuItem>
                <MenuItem value="QC" sx={{ fontSize: '0.75rem' }}>QC</MenuItem>
                <MenuItem value="SK" sx={{ fontSize: '0.75rem' }}>SK</MenuItem>
                <MenuItem value="YT" sx={{ fontSize: '0.75rem' }}>YT</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
          
          {/* Field 6: Term */}
          <Grid item xs={12} sm={6} md={1.7}>
            <TextField
              id="term"
              name="term"
              label="Term"
              value={checklistState.term || ''}
              onChange={(e) => handleTextChange('term')(e.target.value)}
              fullWidth
              InputLabelProps={{ sx: { fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' } }}
              InputProps={{
                sx: {
                  height: '32px',
                  fontSize: '0.75rem',
                  '& .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' }
                }
              }}
            />
          </Grid>
          
          {/* Field 7: Rate */}
          <Grid item xs={12} sm={6} md={1.7}>
            <TextField
              id="rate"
              name="rate"
              label="Rate"
              value={checklistState.rate || ''}
              onChange={(e) => handleTextChange('rate')(e.target.value)}
              InputProps={{
                endAdornment: <span>%</span>,
                sx: {
                  height: '32px',
                  fontSize: '0.75rem',
                  '& .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' }
                }
              }}
              InputLabelProps={{ sx: { fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' } }}
              fullWidth
            />
          </Grid>

          {/* --- Row 2 (Remaining 7 fields) --- */}
          {/* Field 8: Amortization */}
          <Grid item xs={12} sm={6} md={1.7}>
            <TextField
              id="amortization"
              name="amortization"
              label="Amortization"
              value={checklistState.amortization || ''}
              onChange={(e) => handleTextChange('amortization')(e.target.value)}
              fullWidth
              InputLabelProps={{ sx: { fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' } }}
              InputProps={{
                sx: {
                  height: '32px',
                  fontSize: '0.75rem',
                  '& .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' }
                }
              }}
            />
          </Grid>
          
          {/* Field 9: Mortgage Amount */}
          <Grid item xs={12} sm={6} md={1.7}>
            <TextField
              id="mortgageAmount"
              name="mortgageAmount"
              label="Mortgage Amount"
              value={checklistState.mortgageAmount || ''}
              onChange={(e) => handleTextChange('mortgageAmount')(e.target.value)}
              InputProps={{
                startAdornment: <span>$</span>,
                sx: {
                  height: '32px',
                  fontSize: '0.75rem',
                  '& .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' }
                }
              }}
              InputLabelProps={{ sx: { fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' } }}
              fullWidth
            />
          </Grid>
          
          {/* Field 10: MTG Type */}
          <Grid item xs={12} sm={6} md={1.7}>
            <StyledFormControl fullWidth>
              <InputLabel id="mtgtype-label" sx={{ fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' }}>MTG Type</InputLabel>
              <Select
                labelId="mtgtype-label"
                name="mtgType"
                value={checklistState.mtgType}
                label="MTG Type"
                onChange={handleSelectChange('mtgType')}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  '.MuiSelect-select': { padding: '4px 14px' },
                  '.MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  cursor: 'pointer'
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="1st Conventional" sx={{ fontSize: '0.75rem' }}>1st Conventional</MenuItem>
                <MenuItem value="1st" sx={{ fontSize: '0.75rem' }}>1st</MenuItem>
                <MenuItem value="1st High Ratio" sx={{ fontSize: '0.75rem' }}>1st High Ratio</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
          
          {/* Field 11: LTV */}
          <Grid item xs={12} sm={6} md={1.7}>
            <TextField
              id="ltv"
              name="ltv"
              label="LTV"
              value={checklistState.ltv || ''}
              onChange={(e) => handleTextChange('ltv')(e.target.value)}
              InputProps={{
                endAdornment: <span>%</span>,
                sx: {
                  height: '32px',
                  fontSize: '0.75rem',
                  '& .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' }
                }
              }}
              InputLabelProps={{ sx: { fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' } }}
              fullWidth
            />
          </Grid>
          
          {/* Field 12: Employer */}
          <Grid item xs={12} sm={6} md={1.7}>
            <StyledFormControl fullWidth>
              <InputLabel id="emp-watchlist-label" sx={{ fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' }}>Employer</InputLabel>
              <Select
                labelId="emp-watchlist-label"
                name="employerWatchlistStatus"
                value={checklistState.employerWatchlistStatus}
                label="Employer"
                onChange={handleSelectChange('employerWatchlistStatus')}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  '.MuiSelect-select': { padding: '4px 14px' },
                  '.MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  cursor: 'pointer'
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="Employer on watchlist" sx={{ fontSize: '0.75rem' }}>On watchlist</MenuItem>
                <MenuItem value="Employer not on watchlist" sx={{ fontSize: '0.75rem' }}>Not on watchlist</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
          
          {/* Field 13: Match Files */}
          <Grid item xs={12} sm={6} md={1.7}>
            <StyledFormControl fullWidth>
              <InputLabel id="match-files-label" sx={{ fontSize: '0.75rem', transform: 'translate(14px, 8px) scale(1)' }}>Match Files</InputLabel>
              <Select
                labelId="match-files-label"
                name="matchFilesStatus"
                value={checklistState.matchFilesStatus}
                label="Match Files"
                onChange={handleSelectChange('matchFilesStatus')}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  '.MuiSelect-select': { padding: '4px 14px' },
                  '.MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { transition: 'none' },
                  cursor: 'pointer'
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="No match files" sx={{ fontSize: '0.75rem' }}>No match files</MenuItem>
                <MenuItem value="Match files identified" sx={{ fontSize: '0.75rem' }}>Match files identified</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
          
          {/* Field 14: ETO */}
          <Grid item xs={12} sm={6} md={1.7}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checklistState.etoChecked}
                  onChange={handleCheckboxChange('etoChecked')}
                  name="etoChecked"
                  sx={{ padding: '4px' }}
                />
              }
              label={<Typography sx={{ fontSize: '0.75rem', lineHeight: '32px' }}>ETO</Typography>}
              sx={{ height: '32px', marginTop: 0, marginBottom: 0 }}
            />
          </Grid>

          {/* --- Conditional fields below the main grid --- */}
          {checklistState.matchFilesStatus === 'Match files identified' && (
            <Grid item xs={12}>
              <TextField
                id="matchFilesNotes"
                name="matchFilesNotes"
                label="Match Files Notes"
                value={checklistState.matchFilesNotes || ''}
                onChange={(e) => handleTextChange('matchFilesNotes')(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          )}
          {checklistState.propertyValueStatus === 'Other' && (
            <Grid item xs={12}>
              <TextField
                id="propertyValueOtherNotes"
                name="propertyValueOtherNotes"
                label="Other Property Value Notes"
                value={checklistState.propertyValueOtherNotes || ''}
                onChange={(e) => handleTextChange('propertyValueOtherNotes')(e.target.value)}
                fullWidth
              />
            </Grid>
          )}
          {checklistState.etoChecked && (
            <Grid item xs={12}>
              <TextField
                id="etoNotes"
                name="etoNotes"
                label="ETO Notes"
                value={checklistState.etoNotes || ''}
                onChange={(e) => handleTextChange('etoNotes')(e.target.value)}
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default PreliminaryChecklist;
